import React, { useCallback, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import {
  Button,
  FormControl,
  Select,
  Grid,
  MenuItem,
  Modal,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewProduct,
  addUploads,
  getSelectedGroupProducts,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  mobSelected,
  returnQuantity,
  returnType,
} from "features/product/productSlice";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { useEffect } from "react";
import {
  getAttributeProducts,
  getAttributes,
  getDifferentVariants,
  getSameVariants,
} from "utils/api";
import { getLineItemMetadata } from "utils/lineItemMetadata";
import { getApiErrorMessage } from "utils/apiError";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { ReactComponent as Search } from "assets/search.svg";
import {
  StyledDifferentProduct,
  StyledOpenFilter,
  StyledVariantSelect,
} from "./styled";
import {
  addReturnedProducts,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import {
  getSelectedOrder,
  updateWebSelected,
} from "features/orders/orderSlice";
import { toast } from "react-toastify";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import SizeSelector from "./SizeSelector";
import DoneIcon from "@mui/icons-material/Done";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import parse from "html-react-parser";
import LoadingError from "components/v1/common/LoadingError";

function DifferentProduct({
  quantityInc,
  productType,
  requiredProduct,
  setRequiredProduct,
  selectedVariant,
  setSelectedVariant,
  otherReason,
  setAnimate,
  openFilter,
  setOpenFilter,
  setUploads,
}) {
  const [query, setQuery] = useState(null);
  const [offset, setOffset] = useState(0);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [differentProductLoader, setDifferentProductLoader] = useState(true);
  const [differentProducts, setDifferentProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [collectionLoader, setCollectionLoader] = useState(false);
  const [collectionId, setCollectionId] = useState(null);
  const [viewMoreLoader, setViewMoreLoader] = useState(false);
  const [differentApiError, setDifferentApiError] = useState(false);
  const [showViewMore, setShowViewMore] = useState(true);
  const [searchBy, setSearchBy] = useState("title");
  const [skuQuery, setSkuQuery] = useState(null);
  const [searchInput, setSearchInput] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [APIVersion, setAPIVersion] = useState("v1");
  const [variantsLoader, setVariantsLoader] = useState(false);
  const [customProperties, setCustomProperties] = useState([]);
  const [customSizeError, setCustomSizeError] = useState(false);
  const [customProductGroupId, setCustomProductGroupId] = useState("");
  const [activeFilter, setActiveFilter] = useState(false);
  const [allAttributes, setAllAttributes] = useState([]);
  const [openAttributeValues, setOpenAttributeValues] = useState("");
  const [openAttributeId, setOpenAttributeId] = useState("");
  const [attributeData, setAttributeData] = useState([]);
  const [filteredVariantProducts, setFilteredVariantProducts] = useState([]);
  const [selectedFilterVariantId, setSelectedFilterVariantId] = useState(null);
  const [discountPrice, setDiscountPrice] = useState(0);
  const [sameVariantConfirmationOpen, setSameVariantConfirmationOpen] =
    useState(false);

  const mapVariantAttributes = (variant) => {
    const mapping = {};
    if (variant && Array.isArray(variant.attributes)) {
      variant.attributes.forEach((attr) => {
        mapping[attr.product_attribute] = attr.value;
      });
    }
    return mapping;
  };

  const selectedOrder = useSelector(getSelectedOrder);
  const selectedProduct = useSelector(getSelectedProduct);
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const returnedProducts = useSelector(getReturnedProducts);
  const reason = useSelector(getSelectedReason);
  const subReason = useSelector(getSelectedSubReason);
  const selectedGroupProducts = useSelector(getSelectedGroupProducts);

  const dispatch = useDispatch();

  const colors = ["#EAA717", "#DE4B92", "#2871F0", "#16AC37"];

  const isOriginalVariant = useCallback(
    (variant) =>
      variant &&
      selectedProduct &&
      String(variant.variant_id) === String(selectedProduct.variant_id),
    [selectedProduct],
  );

  const isSameVariantSelected =
    isOriginalVariant(selectedVariant);

  const openSameVariantConfirmationIfNeeded = useCallback((variant) => {
    if (isOriginalVariant(variant)) {
      setSameVariantConfirmationOpen(true);
    }
  }, [isOriginalVariant]);

  const isSelectedVariantUnavailable =
    selectedVariant &&
    selectedVariant !== null &&
    !isSameVariantSelected &&
    (selectedVariant.out_of_stock ||
      !selectedVariant.quantity_available_for_exchange ||
      (merchantConfig.merchant_id === 541 &&
        selectedVariant.inventory_quantity < 7));

  const resolveImageUrl = (...candidates) => {
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (typeof candidate === "string") return candidate;
      if (typeof candidate === "object") {
        if (typeof candidate.src === "string") return candidate.src;
        if (typeof candidate.url === "string") return candidate.url;
      }
    }
    return "";
  };

  const callDifferentProducts = (collectionid, newOffset, viewMoreBool) => {
    getDifferentVariants(
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      newOffset,
      collectionid == false ? undefined : collectionid,
      query ? encodeURI(query) : null,
      skuQuery ? encodeURI(skuQuery) : null,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      selectedProduct.variant_id,
      selectedProduct.sku,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        if (resp.data.products.length === 0) {
          setShowViewMore(false);
        }
        if (collectionid !== null && !viewMoreBool) {
          for (let i = 0; i < resp.data.products.length; i++) {
            const sortedVariants = resp.data.products[i].variants.sort(
              (a, b) => a.position - b.position,
            );
            resp.data.products[i].variants = sortedVariants;
          }
          setDifferentProducts(resp.data.products);
          setAPIVersion(resp.data.api_version);
        } else {
          for (let i = 0; i < resp.data.products.length; i++) {
            const sortedVariants = resp.data.products[i].variants.sort(
              (a, b) => a.position - b.position,
            );
            resp.data.products[i].variants = sortedVariants;
          }
          setDifferentProducts(differentProducts.concat(resp.data.products));
          setAPIVersion(resp.data.api_version);
        }
        setDifferentProductLoader(false);
        setViewMoreLoader(false);
        setDifferentApiError(false);
      })
      .catch((err) => {
        if (differentProducts.length > 0) {
          toast.error(
            getApiErrorMessage(err, "Could not load more products. Try again."),
            {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
            },
          );
        } else {
          setDifferentApiError(true);
        }
      });
  };

  const getAllAttributes = () => {
    getAttributes(
      merchantConfig.merchant_id,
      rmsConfig.product_service_version,
    ).then((resp) => {
      setAllAttributes(resp.data);
    });
  };

  useEffect(() => {
    if (selectedProduct?.restricted_collections.length > 0) {
      setCollectionLoader(false);
      setCollections(selectedProduct.restricted_collections);
      setSelectedCollection(0);
      setCollectionId(selectedProduct.restricted_collections[0].id);
      callDifferentProducts(selectedProduct.restricted_collections[0].id);
    } else {
      callDifferentProducts();
    }
    getAllAttributes();
  }, []);

  useEffect(() => {
    if (searchInput == "") {
      setDifferentProductLoader(true);
      if (selectedProduct?.restricted_collections.length > 0) {
        setCollections(selectedProduct.restricted_collections);
        setSelectedCollection(0);
        setCollectionId(selectedProduct.restricted_collections[0].id);
        callDifferentProducts(selectedProduct.restricted_collections[0].id);
      } else {
        callDifferentProducts();
      }
    }
  }, [searchInput]);

  useEffect(() => {
    if (searchBy == "sku") {
      setSkuQuery(searchInput);
      setQuery(null);
    } else {
      setQuery(searchInput);
      setSkuQuery(null);
    }
  }, [searchBy]);

  const handleSearch = (collectionid, newOffset) => {
    getDifferentVariants(
      merchantConfig.merchant_id,
      selectedProduct.product_id,
      quantityInc,
      selectedProduct.product_price,
      newOffset,
      collectionid,
      query ? encodeURI(query) : null,
      skuQuery ? encodeURI(skuQuery) : null,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("lower_value_product")
        : undefined,
      selectedOrder.exchange_configurations.length > 0
        ? selectedOrder.exchange_configurations.includes("higher_value_product")
        : undefined,
      selectedProduct.variant_id,
      selectedProduct.sku,
      rmsConfig.product_service_version,
    )
      .then((resp) => {
        setDifferentProducts(resp.data.products);
        setAPIVersion(resp.data.api_version);
        setDifferentProductLoader(false);
        setViewMoreLoader(false);
        setDifferentApiError(false);
      })
      .catch((err) => {
        setDifferentApiError(true);
      });
  };

  const handleCollectionSearch = (collection, i) => {
    setDifferentProductLoader(true);
    if (selectedCollection !== i) {
      callDifferentProducts(collection.collection_id);
      setSelectedCollection(i);
      setCollectionId(collection.collection_id);
    } else {
      if (selectedProduct?.restricted_collections.length > 0) {
        callDifferentProducts(selectedProduct.restricted_collections[0].id);
        setSelectedCollection(0);
        setCollections(selectedProduct.restricted_collections);
        setCollectionId(selectedProduct.restricted_collections[0].id);
      } else {
        callDifferentProducts();
        setSelectedCollection(null);
        setCollectionId(false);
      }
    }
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      setDifferentProductLoader(true);
      e.preventDefault();
      if (selectedProduct?.restricted_collections.length > 0) {
        handleSearch(selectedProduct?.restricted_collections[0].id);
      } else {
        handleSearch();
      }
    }
  };

  const handleProductExchange = () => {
    if (customSizeError) return;
    dispatch(mobSelected(false));
    dispatch(updateWebSelected(false));
    dispatch(addNewProduct(requiredProduct));
    dispatch(returnQuantity(quantityInc));
    dispatch(returnType("exchange"));
    dispatch(addUploads([]));
    setUploads([]);
    let tempProducts = [...returnedProducts];
    let line_item_ids = tempProducts.map((a) => a.line_item_id);

    if (selectedGroupProducts.length > 0) {
      for (let i = selectedGroupProducts.length - 1; i >= 0; i--) {
        const product = selectedGroupProducts[i];

        const index = line_item_ids.indexOf(product.line_item_id);

        if (index !== -1) {
          tempProducts.splice(index, 1);
          line_item_ids.splice(index, 1);
        }
      }
    } else {
      if (line_item_ids.indexOf(selectedProduct.line_item_id) !== -1) {
        let index = line_item_ids.indexOf(selectedProduct.line_item_id);
        tempProducts.splice(index, 1);
      }
    }

    let childProducts = selectedGroupProducts.filter(
      (groupProduct) =>
        groupProduct.is_child === true &&
        groupProduct.exchange_eligible === true &&
        groupProduct.auto_add === true,
    );

    dispatch(
      addReturnedProducts([
        ...tempProducts,
        {
          merchant_id: merchantConfig.merchant_id,
          line_item_id: selectedProduct.line_item_id,

          product_id: selectedProduct.product_id,
          variant_id: selectedProduct.variant_id,
          product_price: selectedProduct.product_price,
          quantity: quantityInc,
          product_title: selectedProduct.product_title,
          variant_name: selectedProduct.variant_name,
          product_sku: selectedProduct.sku,
          original_product_price: selectedProduct.original_product_price,
          variant_weight: selectedProduct?.variant_weight || "",
          variant_weight_unit: selectedProduct?.variant_weight_unit || "",
          variant_image_url: selectedProduct.image_url,

          exchange_product_id: requiredProduct.store_product_id,
          exchange_variant_id: selectedVariant.variant_id,
          exchange_product_sku: selectedVariant.sku,
          exchange_product_price: selectedVariant.price,
          exchange_product_title: requiredProduct.product_title,
          exchange_variant_image_url:
            (selectedVariant.variant_img && selectedVariant.variant_img.src) ||
            selectedVariant.variant_img ||
            selectedVariant.variant_img.alt,
          exchange_variant_name: selectedVariant.title,
          original_exchange_product_price:
            requiredProduct.original_product_price,
          exchange_variant_weight: selectedVariant?.variant_weight || "",
          exchange_variant_weight_unit:
            selectedVariant?.variant_weight_unit || "",

          return_type: "exchange",
          refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

          customer_reason_code: reason.reason_code,
          customer_reason_label: reason.reason_label,
          customer_reason_comment: otherReason,
          customer_sub_reason_code:
            subReason && subReason !== null ? subReason.reason_code : null,
          customer_sub_reason_label:
            subReason && subReason !== null ? subReason.reason_label : null,

          total_item_discount: selectedProduct.total_item_discount,
          discounted_price: selectedProduct.discounted_price,

          refund_applicable: selectedProduct.refund_applicable,
          exchange_applicable: selectedProduct.exchange_applicable,
          shipment_applicable: selectedProduct.shipment_applicable,

          product_group_id:
            selectedProduct.product_group_id || customProductGroupId,
          is_parent: selectedProduct.is_parent,
          is_child: selectedProduct.is_child,
          properties: customProperties || selectedProduct.properties,

          // Not sure why, Just hard coded optional fields
          variant_length: 0,
          variant_breadth: 0,
          variant_height: 0,
          exchange_variant_length: 0,
          exchange_variant_breadth: 0,
          exchange_variant_height: 0,
          discount_applied: false,
          item_amount_difference: 0,
          total_item_amount_difference: 0,

          remaining_qty: selectedProduct.quantity - quantityInc,
          exchange_type: "different",
          applicable_refund_modes: null,
          ...getLineItemMetadata(selectedProduct),
        },
        ...childProducts.map((childProduct) => ({
          merchant_id: merchantConfig.merchant_id,
          line_item_id: childProduct.line_item_id,

          product_id: childProduct.product_id,
          variant_id: childProduct.variant_id,
          product_price: childProduct.product_price,
          quantity: quantityInc,
          product_title: childProduct.product_title,
          variant_name: childProduct.variant_name,
          product_sku: childProduct.sku,
          original_product_price: childProduct.original_product_price,
          variant_weight: childProduct?.variant_weight || "",
          variant_weight_unit: childProduct?.variant_weight_unit || "",
          variant_image_url: childProduct.image_url,

          exchange_product_id: childProduct.product_id,
          exchange_variant_id: childProduct.variant_id,
          exchange_product_sku: childProduct.sku,
          exchange_product_price: childProduct.product_price,
          exchange_product_title: childProduct.product_title,
          exchange_variant_image_url: childProduct.image_url,
          exchange_variant_name: childProduct.variant_name,
          original_exchange_product_price: childProduct.original_product_price,
          exchange_variant_weight: childProduct?.variant_weight || "",
          exchange_variant_weight_unit: childProduct?.variant_weight_unit || "",

          return_type: "exchange",
          refund_mode_offer_amount: 0, // Not sure why but we give based on line items as well

          customer_reason_code: reason.reason_code,
          customer_reason_label: reason.reason_label,
          customer_reason_comment: otherReason,
          customer_sub_reason_code:
            subReason && subReason !== null ? subReason.reason_code : null,
          customer_sub_reason_label:
            subReason && subReason !== null ? subReason.reason_label : null,

          total_item_discount: childProduct.total_item_discount,
          discounted_price: childProduct.discounted_price,

          exchange_applicable: childProduct.exchange_applicable,
          refund_applicable: childProduct.refund_applicable,
          shipment_applicable: childProduct.shipment_applicable,

          product_group_id: childProduct.product_group_id,
          is_parent: childProduct.is_parent,
          is_child: childProduct.is_child,
          properties: childProduct.properties,

          // Not sure why, Just hard coded optional fields
          variant_length: 0,
          variant_breadth: 0,
          variant_height: 0,
          exchange_variant_length: 0,
          exchange_variant_breadth: 0,
          exchange_variant_height: 0,
          discount_applied: false,
          item_amount_difference: 0,
          total_item_amount_difference: 0,

          remaining_qty: childProduct.quantity - quantityInc,
          exchange_type: "different",
          applicable_refund_modes: null,
          ...getLineItemMetadata(childProduct),
        })),
      ]),
    );

    setAnimate(true);
    // Reset animation after it's done if needed
    setTimeout(() => {
      setAnimate(false);
    }, 700); // Duration matches the animation
  };

  const handleExchangeSelectionClick = () => {
    if (isSameVariantSelected) {
      setSameVariantConfirmationOpen(true);
      return;
    }

    handleProductExchange();
  };

  const hasDiscount =
    rmsConfig.discount_config &&
    Object.keys(rmsConfig.discount_config).length > 0 &&
    rmsConfig.discount_config.target_type === "line_item";

  useEffect(() => {
    if (requiredProduct !== null) {
      let variantToSelect = null;

      if (selectedFilterVariantId) {
        variantToSelect = requiredProduct.variants.find(
          (v) => v.variant_id === selectedFilterVariantId,
        );
      }

      if (!variantToSelect) {
        variantToSelect = requiredProduct.variants.filter(
          (a) => a.variant_id != selectedProduct.variant_id,
        )[0];
      }

      setSelectedVariant(variantToSelect);
      setSelectedCategories(mapVariantAttributes(variantToSelect));
      setSelectedFilterVariantId(null);
    }
  }, [requiredProduct]);

  useEffect(() => {
    if (
      requiredProduct &&
      selectedVariant &&
      selectedVariant?.errors &&
      selectedVariant?.errors.length > 0
    ) {
      document?.querySelector(".out-of-stock")?.scrollIntoView();
    }
  }, [selectedVariant]);

  useEffect(() => {
    if (selectedVariant && selectedVariant !== null) {
      const variant = selectedVariant || requiredProduct.variants[0];
      const originalPrice = Number(variant.price);

      let discountedPrice = originalPrice;

      if (hasDiscount) {
        if (rmsConfig.discount_config.type === "fixed") {
          discountedPrice =
            originalPrice - Number(rmsConfig.discount_config.value);
        } else {
          discountedPrice =
            originalPrice -
            (originalPrice * Number(rmsConfig.discount_config.value)) / 100;
        }
        discountedPrice = discountedPrice.toFixed(2);
        setDiscountPrice(discountedPrice);
      }
    }
  }, [selectedVariant]);

  const MoreLoader = () => {
    return [...Array(6).keys()].map((a) => {
      return (
        <Grid
          justifyContent="center"
          className="different-skeleton"
          item
          key={a}
          xs={6}
          md={4}
        >
          <div className="diif-image">
            <Skeleton style={{ width: "100%", minHeight: "150px" }} />
          </div>
          <Typography>
            <Skeleton />
          </Typography>
          <Typography style={{ margin: "5px 0px" }}>
            <Skeleton />
          </Typography>
          <Typography style={{ margin: "5px 0px" }}>
            <Skeleton />
          </Typography>
        </Grid>
      );
    });
  };

  const viewMore = () => {
    setViewMoreLoader(true);
    callDifferentProducts(collectionId, offset + 12, true);
    setOffset(offset + 12);
  };

  const handleVariant = (event) => {
    const variant = event.target.value;
    setSelectedVariant(variant);
    setSelectedCategories(mapVariantAttributes(variant));
    openSameVariantConfirmationIfNeeded(variant);
  };

  const orderRequiredProduct = (product) => {
    const unorderedProduct = { ...product };

    // Sort the attributes
    unorderedProduct.attributes = unorderedProduct.attributes.sort((a, b) => {
      return a.position - b.position;
    });

    // Sort the 'values' array inside each attribute
    unorderedProduct.attributes.forEach((attribute) => {
      if (Array.isArray(attribute.values)) {
        attribute.values = attribute.values.sort((a, b) => a.order - b.order);
      }
    });

    const initialSelectedValues = {};

    unorderedProduct.attributes.forEach((category) => {
      initialSelectedValues[category.id] = category?.values?.[0]?.id;
    });

    const findSelectedVariant = (variants, attributes) => {
      return variants.find((variant) => {
        return attributes.every((attribute) => {
          const matchingVariantAttribute = variant.attributes.find(
            (variantAttribute) =>
              variantAttribute.product_attribute === attribute.id &&
              attribute.values.some(
                (value) => value.id === variantAttribute.value,
              ),
          );
          return Boolean(matchingVariantAttribute);
        });
      });
    };

    const defaultSelectedVariant = findSelectedVariant(
      unorderedProduct.variants,
      unorderedProduct.attributes,
    );

    setRequiredProduct(unorderedProduct);
    setSelectedCategories(initialSelectedValues);
    setSelectedVariant(defaultSelectedVariant);
  };

  const handleSelectCategory = (categoryId, value) => {
    // Update selected categories
    const updatedSelectedCategories = {
      ...selectedCategories,
      [categoryId]: value,
    };

    // Sort the selected categories by the category order (position) in `categories`
    const sortedSelectedCategories = Object.keys(updatedSelectedCategories)
      .sort((a, b) => {
        const positionA = requiredProduct.attributes.find(
          (cat) => cat.id === a,
        )?.position;
        const positionB = requiredProduct.attributes.find(
          (cat) => cat.id === b,
        )?.position;
        return positionA - positionB;
      })
      .reduce((acc, key) => {
        acc[key] = updatedSelectedCategories[key];
        return acc;
      }, {});

    // Set the sorted and normalized categories
    setSelectedCategories(sortedSelectedCategories);

    const findSelectedVariantWithMapping = (variants, selectedAttributes) => {
      return variants.find((variant) => {
        return Object.entries(selectedAttributes).every(
          ([productAttribute, value]) => {
            return variant.attributes.some(
              (variantAttribute) =>
                variantAttribute.product_attribute ===
                  parseInt(productAttribute, 10) &&
                variantAttribute.value === value,
            );
          },
        );
      });
    };

    const matchedVariant = findSelectedVariantWithMapping(
      requiredProduct.variants,
      sortedSelectedCategories,
    );

    if (matchedVariant) {
      setSelectedVariant(matchedVariant);
      openSameVariantConfirmationIfNeeded(matchedVariant);
    }
  };

  const getProductDetails = (product) => {
    if (APIVersion === "v1") {
      setRequiredProduct(product);
      setVariantsLoader(false);
    } else {
      getSameVariants(
        merchantConfig.merchant_id,
        product.product_id,
        quantityInc,
        selectedProduct.product_price,
        selectedProduct.variant_id,
        selectedProduct.sku,
        selectedOrder.exchange_configurations.length > 0
          ? selectedOrder.exchange_configurations.includes(
              "lower_value_product",
            )
          : undefined,
        selectedOrder.exchange_configurations.length > 0
          ? selectedOrder.exchange_configurations.includes(
              "higher_value_product",
            )
          : undefined,
        rmsConfig.product_service_version,
      )
        .then((resp) => {
          orderRequiredProduct(resp.data.products);
          setVariantsLoader(false);
        })
        .catch(() => {
          setDifferentApiError(true);
        });
    }
  };

  const CategoryDisplay = ({ category }) => {
    return (
      <div style={{ margin: "10px 0px" }}>
        <p style={{ color: "#3b444d", fontSize: "16px", fontWeight: 700 }}>
          {category.display_name}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {category.values.map((attr) => {
            return (
              <div key={attr.id} style={{ position: "relative" }}>
                {/* Hidden radio button */}
                <input
                  type="radio"
                  id={`${category.id}-${attr.id}`}
                  name={category.name}
                  value={attr.value}
                  checked={selectedCategories[category.id] === attr.id}
                  onChange={() => handleSelectCategory(category.id, attr.id)}
                  style={{ display: "none" }} // Hide the radio button
                />
                <p
                  onClick={() => handleSelectCategory(category.id, attr.id)}
                  style={{
                    border: `1px solid ${merchantConfig.primary_theme_color}`,
                    backgroundColor:
                      selectedCategories[category.id] === attr.id
                        ? merchantConfig.primary_theme_color
                        : "transparent",
                    color:
                      selectedCategories[category.id] === attr.id
                        ? "#fff"
                        : merchantConfig.primary_theme_color,
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "4px"
                      : "0px",
                    padding: "0px 8px",
                    margin: "5px 8px 5px 0px",
                    minWidth: "30px",
                    minHeight: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  {attr.display_value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const isValueSelected = (valueId) => {
    return attributeData.some((attr) => attr.values.includes(valueId));
  };

  return (
    <div>
      {variantsLoader ? (
        <div
          style={{
            display: "flex",
            padding: "10px 20px",
            alignItems: "center",
          }}
        >
          <Skeleton style={{ width: "200px", height: "200px" }} />
          <div style={{ width: "100%", marginLeft: "20px" }}>
            <Typography>
              <Skeleton />
            </Typography>
            <Typography style={{ margin: "5px 0px" }}>
              <Skeleton />
            </Typography>
            <Typography style={{ margin: "5px 0px" }}>
              <Skeleton />
            </Typography>
          </div>
        </div>
      ) : requiredProduct === null ? (
        <div>
          {openFilter && (
            <StyledOpenFilter>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 1.5rem",
                  justifyContent: "space-between",
                }}
              >
                <p
                  style={{
                    fontWeight: "600",
                    color: "#3B444D",
                    fontSize: "18px",
                  }}
                >
                  Filters
                </p>
                <Button
                  variant="text"
                  color="primary"
                  style={{
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "4px"
                      : "0px",
                  }}
                  onClick={() => {
                    setAttributeData([]);
                    setFilteredVariantProducts([]);
                    setOpenFilter(false);
                    setDifferentProductLoader(true);
                    setActiveFilter(false);
                    if (selectedProduct?.restricted_collections.length > 0) {
                      setCollectionLoader(false);
                      setCollections(selectedProduct.restricted_collections);
                      setSelectedCollection(0);
                      setCollectionId(
                        selectedProduct.restricted_collections[0].id,
                      );
                      callDifferentProducts(
                        selectedProduct.restricted_collections[0].id,
                      );
                    } else {
                      callDifferentProducts();
                    }
                  }}
                >
                  CLEAR ALL
                </Button>
              </div>
              <hr />
              <div style={{ display: "flex" }}>
                <div className="filter-attributes">
                  {allAttributes.length > 0 ? (
                    <div>
                      {allAttributes.map((attribute) => {
                        return (
                          <>
                            <p
                              style={{
                                padding: "0px 1.5rem",
                                background:
                                  openAttributeId === attribute.id
                                    ? "#fff"
                                    : "#f5f5f5",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                height: "45px",
                              }}
                              onClick={() => {
                                setOpenAttributeValues(attribute.values);
                                setOpenAttributeId(attribute.id);
                              }}
                            >
                              <span
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  lineHeight: "1.2rem",
                                  maxHeight: "2.4rem",
                                  width: "100%",
                                }}
                              >
                                {attribute.display_name}
                              </span>
                            </p>
                            <hr
                              style={{
                                height: "0",
                                borderTop: "1px solid #3B444D33",
                              }}
                            />
                          </>
                        );
                      })}
                    </div>
                  ) : (
                    <p>No Filters Found</p>
                  )}
                </div>
                <div className="filter-attribute-values">
                  {openAttributeValues.length > 0 ? (
                    <div>
                      {openAttributeValues.map((attributeValue) => {
                        return (
                          <>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "0px 0.5rem",
                                height: "45px",
                                cursor: "pointer",
                                width: "100%",
                              }}
                              onClick={() => {
                                setAttributeData((prev) => {
                                  const existingAttr = prev.find(
                                    (item) => item.id === openAttributeId,
                                  );

                                  if (existingAttr) {
                                    const valueExists =
                                      existingAttr.values.includes(
                                        attributeValue.id,
                                      );

                                    // Remove the value if it exists
                                    const updatedValues = valueExists
                                      ? existingAttr.values.filter(
                                          (v) => v !== attributeValue.id,
                                        )
                                      : [
                                          ...existingAttr.values,
                                          attributeValue.id,
                                        ];

                                    if (updatedValues.length === 0) {
                                      // Remove the entire attribute if values is empty
                                      return prev.filter(
                                        (item) => item.id !== openAttributeId,
                                      );
                                    }

                                    return prev.map((item) =>
                                      item.id === openAttributeId
                                        ? { ...item, values: updatedValues }
                                        : item,
                                    );
                                  } else {
                                    // Add new attribute with the selected value
                                    return [
                                      ...prev,
                                      {
                                        id: openAttributeId,
                                        values: [attributeValue.id],
                                      },
                                    ];
                                  }
                                });
                              }}
                            >
                              <DoneIcon
                                style={{
                                  color: isValueSelected(attributeValue.id)
                                    ? merchantConfig.primary_theme_color !==
                                      "#000"
                                      ? merchantConfig.primary_theme_color
                                      : "#2871f0"
                                    : "#bbb",
                                  fontSize: "18px",
                                }}
                              />
                              <p
                                style={{
                                  display: "flex",
                                  width: "100%",
                                  marginLeft: "10px",
                                  alignItems: "center",
                                  fontWeight: isValueSelected(attributeValue.id)
                                    ? "600"
                                    : "400",
                                  color: isValueSelected(attributeValue.id)
                                    ? merchantConfig.primary_theme_color !==
                                      "#000"
                                      ? merchantConfig.primary_theme_color
                                      : "#2871f0"
                                    : "#3B444D",
                                }}
                              >
                                {attributeValue.display_value}
                              </p>
                            </div>
                            <hr
                              style={{
                                height: "0",
                                borderTop: "1px solid #fff",
                              }}
                            />
                          </>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ marginTop: "1rem" }}>No Filter selected</p>
                  )}
                </div>
              </div>
              <div
                style={{ bottom: "0px", position: "absolute", width: "100%" }}
              >
                <Button
                  variant="text"
                  color="primary"
                  style={{
                    width: "50%",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "4px"
                      : "0px",
                  }}
                  onClick={() => setOpenFilter(false)}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    width: "50%",
                    borderRadius: merchantConfig.use_rounded_edge_buttons
                      ? "4px"
                      : "0px",
                  }}
                  onClick={() => {
                    setOpenFilter(false);
                    setDifferentProductLoader(true);
                    if (attributeData.length > 0) {
                      setActiveFilter(true);
                      getAttributeProducts(
                        merchantConfig.merchant_id,
                        rmsConfig.product_service_version,
                        attributeData,
                      ).then((resp) => {
                        setFilteredVariantProducts(resp.data);
                        setDifferentProductLoader(false);
                      });
                    } else {
                      setActiveFilter(false);
                      if (selectedProduct?.restricted_collections.length > 0) {
                        setCollectionLoader(false);
                        setCollections(selectedProduct.restricted_collections);
                        setSelectedCollection(0);
                        setCollectionId(
                          selectedProduct.restricted_collections[0].id,
                        );
                        callDifferentProducts(
                          selectedProduct.restricted_collections[0].id,
                        );
                      } else {
                        callDifferentProducts();
                      }
                    }
                  }}
                >
                  Apply
                </Button>
              </div>
            </StyledOpenFilter>
          )}

          <div
            style={{
              background: "#fff",
              position: "sticky",
              maxWidth: "inherit",
              top: "65px",
              margin: "0px",
              padding: "1rem 1rem 0.75rem",
              zIndex: "9",
            }}
          >
            <div
              style={{
                background: "#F5F5F5",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 0rem",
                  flex: 1,
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    mr: 1,
                    minWidth: 75,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                >
                  <Select
                    value={searchBy}
                    onChange={(e) => setSearchBy(e.target.value)}
                    sx={{ fontSize: "12px", border: "none" }}
                  >
                    <MenuItem value="title" sx={{ fontSize: "12px" }}>
                      Title
                    </MenuItem>
                    <MenuItem value="sku" sx={{ fontSize: "12px" }}>
                      SKU
                    </MenuItem>
                  </Select>
                </FormControl>
                <input
                  type="text"
                  placeholder={`Search with product ${searchBy}`}
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    if (searchBy == "sku") {
                      setSkuQuery(e.target.value);
                      setQuery(null);
                    } else {
                      setQuery(e.target.value);
                      setSkuQuery(null);
                    }
                  }}
                  onKeyPress={(e) => handleEnter(e)}
                  style={{
                    border: "none",
                    background: "transparent",
                    width: "100%",
                    padding: "0px 0px",
                    marginLeft: "0px",
                    outline: "none",
                    fontFamily:
                      merchantConfig &&
                      merchantConfig.google_font_family !== null
                        ? merchantConfig.google_font_family
                        : "Inter",
                  }}
                />
                {searchInput !== null &&
                  searchInput.length > 0 &&
                  searchInput !== "" && (
                    <p
                      style={{ cursor: "pointer", margin: "6px 8px 0px 0px" }}
                      onClick={() => {
                        setSearchInput("");
                        setQuery("");
                        setSkuQuery("");
                      }}
                    >
                      {" "}
                      <CloseIcon
                        style={{
                          width: "18px",
                          height: "18px",
                          stroke: "#3B444D",
                        }}
                      />{" "}
                    </p>
                  )}
              </div>
              <div
                style={{
                  background: merchantConfig.primary_theme_color,
                  padding: "6px 10px",
                }}
                onClick={() => {
                  setDifferentProductLoader(true);
                  handleSearch();
                }}
              >
                <SearchIcon
                  style={{
                    color: merchantConfig.secondary_theme_color,
                    cursor: "pointer",
                    marginTop: "4px",
                    width: "20px",
                    height: "20px",
                  }}
                />
              </div>
              <div
                style={{
                  background: "#f5f5f5",
                  padding: "7px 10px",
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={() => {
                  setOpenFilter(true);
                }}
              >
                <FilterAltOutlinedIcon
                  style={{
                    color: merchantConfig.primary_theme_color,
                    marginTop: "2px",
                    width: "20px",
                    height: "20px",
                  }}
                />
                {activeFilter && (
                  <div
                    style={{
                      width: "1rem",
                      height: "1rem",
                      background: "red",
                      position: "absolute",
                      top: "-5px",
                      right: "-5px",
                      borderRadius: "50%",
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>

          {collectionLoader ? (
            <Skeleton
              variant="rounded"
              height={30}
              style={{ margin: "20px 16px 0px", borderRadius: "16px" }}
            />
          ) : (
            <div className="collections-section">
              {collections.map((collection, i) => {
                return (
                  <p
                    onClick={() => {
                      handleCollectionSearch(collection, i);
                    }}
                    key={collection.id}
                    style={{
                      padding: "4px 10px",
                      background: `${colors[i % 4]}33`,
                      width: "fit-content",
                      whiteSpace: "nowrap",
                      borderRadius: "20px",
                      marginRight: "10px",
                      fontSize: "12px",
                      cursor: "pointer",
                      border:
                        selectedCollection === i
                          ? `2px solid ${colors[i % 4]}`
                          : "none",
                    }}
                  >
                    {collection.title}
                  </p>
                );
              })}
            </div>
          )}

          <div>
            {differentProductLoader ? (
              <Grid
                container
                gridTemplateColumns="repeat(12, 1fr)"
                columnSpacing={3}
                rowSpacing={0}
                columns={12}
                style={{
                  padding: "0px 24px",
                  justifyContent: "center",
                  marginTop: "0px",
                }}
              >
                {" "}
                <MoreLoader />{" "}
              </Grid>
            ) : differentApiError ? (
              <LoadingError marginTop="100px" />
            ) : filteredVariantProducts.length > 0 ? (
              <div>
                <Grid
                  container
                  gridTemplateColumns="repeat(12, 1fr)"
                  columnSpacing={3}
                  rowSpacing={{ xs: 2 }}
                  columns={12}
                  style={{
                    padding: "0px 24px",
                    marginTop: "0px",
                  }}
                >
                  {filteredVariantProducts.map((product, i) => {
                    const filteredProductImage = resolveImageUrl(
                      product.variant_img,
                      product.images?.[0],
                      product.variants?.[0]?.variant_img,
                      product.variants?.[0]?.src,
                    );

                    return (
                      <Grid
                        xs={6}
                        md={4}
                        sx={{
                          cursor: "pointer",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        item
                        onClick={() => {
                          setSelectedFilterVariantId(product.variant_id);
                          setVariantsLoader(true);
                          setSelectedFilterVariantId(product.variant_id);
                          getProductDetails(product);
                        }}
                        key={i}
                      >
                        <div
                          style={{
                            width: "100%",
                            margin: "10px 0px 5px",
                            height: "190px",
                            minHeight: "190px",
                            maxHeight: "190px",
                            background: `${merchantConfig.primary_theme_color}11`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {filteredProductImage && (
                            <LazyLoadImage
                              effect="opacity"
                              src={filteredProductImage}
                              alt={
                                product?.variant_img?.alt ||
                                product.product_title
                              }
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          )}
                        </div>
                        <p
                          style={{
                            margin: "5px 0px",
                            color: "#3B444D",
                            fontSize: "14px",
                          }}
                        >
                          {product.product_title}
                        </p>
                        <p
                          style={{
                            color: "#3B444D",
                            fontSize: "16px",
                            margin: "auto 0px 5px",
                            fontWeight: "600",
                          }}
                        >
                          ₹ {product.price}
                        </p>
                      </Grid>
                    );
                  })}
                </Grid>
              </div>
            ) : differentProducts.length > 0 ? (
              <Grid
                container
                gridTemplateColumns="repeat(12, 1fr)"
                columnSpacing={3}
                rowSpacing={{ xs: 2 }}
                columns={12}
                style={{
                  padding: "0px 24px",
                  marginTop: "0px",
                }}
              >
                {differentProducts.map((product, i) => {
                  const differentProductImage = resolveImageUrl(
                    product.images?.[0],
                    product.variants?.[0]?.src,
                    product.variants?.[0]?.variant_img,
                  );

                  return (
                    <Grid
                      xs={6}
                      md={4}
                      sx={{
                        display: "flex",
                        cursor: "pointer",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                      item
                      onClick={() => {
                        setSelectedFilterVariantId(null);
                        setVariantsLoader(true);
                        getProductDetails(product);
                      }}
                      key={i}
                    >
                      <div
                        style={{
                          width: "100%",
                          margin: "10px 0px 5px",
                          height: "190px",
                          minHeight: "190px",
                          maxHeight: "190px",
                          background: `${merchantConfig.primary_theme_color}22`,
                          borderRadius: merchantConfig.use_rounded_edge_buttons
                            ? "10px"
                            : "0px",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                          border: "5px solid #f1f1f1",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        {differentProductImage && (
                          <LazyLoadImage
                            effect="opacity"
                            src={differentProductImage}
                            alt={
                              product.images?.[0]?.alt || product.product_title
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        )}
                      </div>
                      <div
                        style={{
                          width: "100%",
                          textAlign: "left",
                          flexGrow: 1, // ✅ Ensures consistent height distribution
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          marginTop: "auto",
                        }}
                      >
                        <p
                          style={{
                            margin: "5px 0px 0px",
                            color: "#3B444D",
                            fontSize: "14px",
                            display: "-webkit-box", // ✅ enable flex-like layout for clamping
                            WebkitLineClamp: 2, // ✅ allow only 2 lines
                            WebkitBoxOrient: "vertical", // ✅ orient lines vertically
                            overflow: "hidden", // ✅ hide overflow text
                            textOverflow: "ellipsis", // ✅ add the ellipsis
                            lineHeight: "1.4em", // ✅ control line spacing
                            minHeight: "2.8em",
                          }}
                        >
                          {product.product_title}
                        </p>
                        <p
                          style={{
                            margin: "2px 0px 5px",
                            color: "#3B444D",
                            fontSize: "12px",
                            width: "100%",
                            overflow: "hidden",
                          }}
                        >
                            {product?.variants?.length} variant{product?.variants?.length > 1 && "s"}
                        </p>
                        <p
                          style={{
                            color: "#3B444D",
                            fontSize: "16px",
                            margin: "0 0px 5px",
                            fontWeight: "600",
                          }}
                        >
                          ₹ {product.variants[0].price}
                        </p>
                        {product.out_of_stock && (
                          <p
                            className="out-of-stock"
                            style={{
                              color: "red",
                              background: "#FFF5F8",
                              border: "1px solid red",
                              borderRadius:
                                merchantConfig.use_rounded_edge_buttons
                                  ? "5px"
                                  : "0px",
                              padding: "6px",
                              fontSize: "12px",
                              fontWeight: "300",
                              marginBottom: "8px",
                            }}
                          >
                            {selectedVariant?.errors &&
                            selectedVariant.errors.length > 0
                              ? selectedVariant.errors.map((error) => {
                                  return (
                                    <p key={error.error_code}>
                                      {error.error_code_message}
                                    </p>
                                  );
                                })
                              : "Out of stock"}
                          </p>
                        )}
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Grid
                container
                gridTemplateColumns="repeat(12, 1fr)"
                columnSpacing={3}
                rowSpacing={{ xs: 2 }}
                columns={12}
                style={{
                  padding: "0px 24px",
                  justifyContent: "center",
                  marginTop: "0px",
                }}
              >
                <Grid
                  item
                  style={{
                    justifyContent: "center",
                    textAlign: "center",
                    marginTop: "4rem",
                  }}
                >
                  <Search />
                  <p style={{ textAlign: "center", marginTop: "1rem" }}>
                    {(query !== null && query.length > 0 && query !== "") ||
                    collectionId !== null
                      ? "Hmm we don't have that..yet"
                      : "Start typing to search new products"}
                  </p>
                </Grid>
              </Grid>
            )}
            {viewMoreLoader && (
              <Grid
                container
                gridTemplateColumns="repeat(12, 1fr)"
                columnSpacing={3}
                rowSpacing={{ xs: 2 }}
                columns={12}
                style={{
                  padding: "0px 24px",
                  justifyContent: "center",
                  marginTop: "0px",
                }}
              >
                <MoreLoader />
              </Grid>
            )}
          </div>

          {!differentApiError &&
            showViewMore &&
            differentProducts.length > 9 &&
            !activeFilter && (
              <p
                style={{
                  textAlign: "center",
                  color: merchantConfig.primary_theme_color,
                  textDecoration: "underline",
                  margin: "60px 0px 5px",
                  cursor: "pointer",
                }}
                onClick={() => viewMore()}
              >
                View More
              </p>
            )}
        </div>
      ) : (
        <div style={{ margin: "10px 0px" }}>
          <StyledDifferentProduct>
            <div
              className="image-container"
              style={{
                background: `${merchantConfig.primary_theme_color}22`,
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
              }}
            >
              <LazyLoadImage
                effect="opacity"
                className="product-image"
                src={resolveImageUrl(
                  selectedVariant && selectedVariant !== null
                    ? selectedVariant.variant_img
                    : requiredProduct.variants[0]?.variant_img,
                  selectedVariant && selectedVariant !== null
                    ? selectedVariant.src
                    : requiredProduct.variants[0]?.src,
                  requiredProduct.images[0],
                )}
                alt="Product-preview"
              />
            </div>
            <div className="product-details">
              <p
                className="product-title"
                style={{ color: merchantConfig.primary_theme_color }}
              >
                {requiredProduct.product_title || requiredProduct.title}
              </p>
              <p
                style={{
                  color: "#3B444D",
                  fontSize: "16px",
                }}
              >
                {(selectedVariant !== null
                  ? selectedVariant.title
                  : requiredProduct.variants[0].title
                ).indexOf("Default") === -1
                  ? selectedVariant !== null
                    ? selectedVariant.title
                    : requiredProduct.variants[0].title
                  : requiredProduct.product_title}
              </p>
              <p
                style={{
                  display: "flex",
                  color: "#3B444D",
                  margin: "6px 0px",
                  alignItems: "center",
                  fontWeight: "700",
                }}
              >
                {hasDiscount && <span>₹ {discountPrice}</span>}
                <span
                  style={{
                    textDecoration: hasDiscount ? "line-through" : "none",
                    marginLeft: hasDiscount ? "10px" : "0px",
                    color: hasDiscount ? "#3B444D66" : "#3B444D",
                  }}
                >
                  ₹{" "}
                  {selectedVariant !== null
                    ? selectedVariant.price
                    : requiredProduct.variants[0].price}{" "}
                </span>
              </p>
              {merchantConfig.merchant_id === 977 && (
                <div>
                  {requiredProduct.attributes.length > 0 &&
                    requiredProduct.attributes
                      .filter((category) => category.name !== "size")
                      .map((category) => {
                        return <CategoryDisplay category={category} />;
                      })}
                  {requiredProduct.attributes.length > 0 &&
                    requiredProduct.attributes
                      .filter((category) => category.name === "size")
                      .map((category) => {
                        const groupByType = (items) => {
                          return items.reduce((acc, item) => {
                            const types = ["single", "double", "queen", "king"];
                            const type = types.find((t) =>
                              item.value.includes(t),
                            );
                            if (!type) return acc;

                            const cleanedDisplayValue = item.display_value
                              // Remove height (last dimension) using regex
                              // .replace(/\s*x\s*\d+\s*inch/, "")
                              // Remove type label in brackets (case-insensitive)
                              .replace(/\s*\(\w+\)\s*$/, "")
                              .trim();

                            const formattedItem = {
                              ...item,
                              group: type,
                              display_value: cleanedDisplayValue,
                            };

                            acc[type] = acc[type] || [];
                            acc[type].push(formattedItem);
                            return acc;
                          }, {});
                        };

                        // Usage
                        const grouped = groupByType(category.values);

                        if (Object.keys(grouped).length > 0) {
                          return (
                            <SizeSelector
                              grouped={grouped}
                              sizeAttributeId={category.id}
                              onCustomSizeError={setCustomSizeError}
                              // relatedProducts={requiredProduct.related_products}
                              // title={
                              //   requiredProduct?.secondary_display_title ||
                              //   requiredProduct?.product_title ||
                              //   requiredProduct.title
                              // }
                              // getProductDetails={getProductDetails}
                              // setVariantsLoader={setVariantsLoader}
                              variants={requiredProduct.variants}
                              setSelectedVariant={setSelectedVariant}
                              onVariantSelected={
                                openSameVariantConfirmationIfNeeded
                              }
                              selectedVariant={selectedVariant}
                              setCustomProperties={setCustomProperties}
                              selectedProduct={selectedProduct}
                              setCustomProductGroupId={setCustomProductGroupId}
                            />
                          );
                        } else {
                          <CategoryDisplay category={category} />;
                        }
                      })}
                </div>
              )}
              {APIVersion === "v2" &&
                merchantConfig.merchant_id !== 977 &&
                requiredProduct.attributes.length > 0 &&
                requiredProduct.attributes.map((category) => {
                  return <CategoryDisplay category={category} />;
                })}
              {requiredProduct.variants.length >= 10 && APIVersion === "v1" && (
                <TextField
                  style={{
                    width: "100%",
                    margin: "15px 0px",
                    maxWidth: "280px",
                  }}
                  select
                  variant="outlined"
                  value={selectedVariant}
                  onChange={handleVariant}
                >
                  {requiredProduct.variants
                    .filter((a) => {
                      return !reason.same_variant_exchange
                        ? a.variant_id != selectedProduct.variant_id
                        : true;
                    })
                    .map((variant) => {
                      return (
                        <MenuItem value={variant} key={variant.id}>
                          {variant.title}
                        </MenuItem>
                      );
                    })}
                </TextField>
              )}

              {selectedVariant &&
                selectedVariant !== null &&
                (selectedVariant.out_of_stock ||
                  !selectedVariant.quantity_available_for_exchange ||
                  (merchantConfig.merchant_id === 541 &&
                    selectedVariant.inventory_quantity < 7)) && (
                  <div
                    className="out-of-stock"
                    style={{
                      color: "#A02923",
                      background: "#FCF2F2",
                      border: "1px solid #F4C9C9",
                      borderRadius: merchantConfig.use_rounded_edge_buttons
                        ? "6px"
                        : "0px",
                      padding: "1rem",
                      display: "flex",
                    }}
                  >
                    <ErrorOutlineRoundedIcon
                      style={{ fontSize: "18px", color: "#E63E36" }}
                    />
                    <div style={{ marginLeft: "6px" }}>
                      <p
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        Product not available for exchange
                      </p>

                      {selectedVariant?.errors &&
                      selectedVariant.errors.length > 0
                        ? selectedVariant.errors.map((error) => {
                            return (
                              <p
                                style={{ fontSize: "12px" }}
                                key={error.error_code}
                              >
                                - {error.error_code_message}
                              </p>
                            );
                          })
                        : "Out of stock"}
                    </div>
                  </div>
                )}
              {isSameVariantSelected && (
                  <div
                    style={{
                      background: "#FFFBEB",
                      border: "1px solid #FEE686",
                      padding: "1rem",
                      color: "#963D1F",
                      display: "flex",
                      borderRadius: merchantConfig.use_rounded_edge_buttons
                        ? "6px"
                        : "0px",
                    }}
                  >
                    <WarningAmberRoundedIcon
                      style={{ fontSize: "18px", color: "#E17433" }}
                    />
                    <div style={{ marginLeft: "6px" }}>
                      <p
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        Same variant selected for exchange
                      </p>
                      <p style={{ fontSize: "12px" }}>
                        You have selected the same variant{" "}
                        {selectedProduct.variant_name} that you originally
                        purchased.
                      </p>
                    </div>
                  </div>
                )}
            </div>
          </StyledDifferentProduct>

          {requiredProduct.variants.length < 10 &&
            requiredProduct.variants.length > 1 &&
            APIVersion === "v1" && (
              <StyledVariantSelect>
                <p
                  style={{
                    margin: "10px 0px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Select Variant
                </p>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {requiredProduct.variants
                    .filter((a) => {
                      return !reason.same_variant_exchange
                        ? a.variant_id != selectedProduct.variant_id
                        : true;
                    })
                    .map((variant) => {
                      return (
                        <Button
                          disableRipple
                          variant={
                            selectedVariant !== null &&
                            selectedVariant.id === variant.id
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => {
                            setSelectedVariant(variant);
                            openSameVariantConfirmationIfNeeded(variant);
                            setSelectedCategories(
                              mapVariantAttributes(variant),
                            );
                          }}
                          style={{
                            fontSize: "12px",
                            minWidth: "50px",
                            width: "fit-content",
                            margin: "8px",
                            marginLeft: "0px",
                            background:
                              selectedVariant !== null &&
                              selectedVariant.id === variant.id
                                ? `${merchantConfig.primary_theme_color}1A`
                                : "transparent",
                            color:
                              selectedVariant !== null &&
                              selectedVariant.id === variant.id
                                ? `#000`
                                : `${merchantConfig.primary_theme_color}`,
                          }}
                          key={variant.id}
                        >
                          {variant.title}
                        </Button>
                      );
                    })}
                </div>
              </StyledVariantSelect>
            )}

          <div
            style={{ padding: "0px 1rem", fontSize: "14px", marginTop: "10px" }}
          >
            {parse(requiredProduct.rich_description)}
          </div>
          <div className="diff-prod-buttons">
            <div
              style={{
                display: "flex",
                position: "absolute",
                width: "100%",
                maxWidth: "600px",
                bottom: 0,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                className="back-button"
                onClick={() => setRequiredProduct(null)}
                style={{
                  borderRadius: merchantConfig.use_rounded_edge_buttons
                    ? "0px 0px 0px 10px"
                    : "0px",
                  background: "#fff",
                }}
              >
                Back
              </Button>
              <Button
                className="exchange-button-web"
                color="primary"
                onClick={handleExchangeSelectionClick}
                variant="contained"
                endIcon={
                  <ArrowForwardIcon
                    sx={{ position: "relative", top: "-2px" }}
                  />
                }
                disabled={customSizeError || isSelectedVariantUnavailable}
                style={{
                  borderRadius: merchantConfig.use_rounded_edge_buttons
                    ? "0px 0px 10px 0px"
                    : "0px",
                }}
              >
                Select for exchange
              </Button>
            </div>
            <div className="exchange-button-mob">
              <Button
                color="primary"
                fullWidth
                onClick={handleExchangeSelectionClick}
                variant="contained"
                endIcon={
                  <ArrowForwardIcon
                    sx={{ position: "relative", top: "-2px" }}
                  />
                }
                style={{
                  transform: "translateX(-50%)",
                  left: "50%",
                  padding: "12px 0px",
                  borderRadius: merchantConfig.use_rounded_edge_buttons
                    ? "0px 0px 10px 10px"
                    : "0px",
                }}
                disabled={customSizeError || isSelectedVariantUnavailable}
              >
                Select for exchange
              </Button>
            </div>
          </div>

          <Modal
            aria-labelledby="same-variant-confirmation-title"
            aria-describedby="same-variant-confirmation-description"
            open={sameVariantConfirmationOpen}
            onClose={() => setSameVariantConfirmationOpen(false)}
            closeAfterTransition
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                minWidth: "300px",
                padding: "20px 24px 24px",
                background: "#fff",
                maxWidth: "460px",
                width: "90%",
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "16px",
                }}
              >
                <div>
                  <p
                    id="same-variant-confirmation-title"
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    Same size selected
                  </p>
                  <p
                    id="same-variant-confirmation-description"
                    style={{ color: "#3B444D", fontSize: "14px" }}
                  >
                    You selected the same size{" "}
                    {selectedProduct?.variant_name || ""} that you originally
                    purchased. Do you want to continue with this exchange?
                  </p>
                </div>
                <CloseIcon
                  style={{
                    width: "20px",
                    height: "20px",
                    stroke: "#3B444D",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  onClick={() => setSameVariantConfirmationOpen(false)}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "24px",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setSameVariantConfirmationOpen(false)}
                >
                  No
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setSameVariantConfirmationOpen(false);
                    handleProductExchange();
                  }}
                >
                  Yes, continue
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
}

export default DifferentProduct;
